/**
 * This is a copy pasta from https://git.corp.stripe.com/stripe-internal/pay-server/blob/HEAD/frontend/analytics-js/src/utils/batchLogger.ts#L1
 */

interface AnalyticsEvent {
  [key: string]: unknown;
}

export type AnalyticsConfig = {
  // Your service's client_id parameter, as configured in client_config.yaml
  // This is the only required config parameter.
  clientId: string;

  // Enables batched requests to AEL's batching endpoint, r.stripe.com/b
  batchSettings?: {
    enableBatching?: boolean;
    batchSize?: number; // optional parameter for batch size, since each client receives varying levels of traffic and requests per second
    batchTimeout?: number; // optional parameter for batch timeout, since each client receives varying levels of traffic and requests per second
  };
};

// Analytics Event Logger Batched Endpoint
const batchProdUrl = 'https://r.stripe.com/b';

export const MAX_BATCH_SIZE = 5;
// Chrome's maximum payload size for sendBeacon() and keepalive fetch is 64kB
// AEL backend also contains a 100,000 kB limit:
// https://git.corp.stripe.com/stripe-internal/zoolander/blob/HEAD/uppsala/src/main/java/com/stripe/dscore/analyticseventlogger/server/translation/HttpServer.java#L87
const MAX_PAYLOAD_SIZE = 64000;
// Timeout after which to flush the buffer, even if the max batch size isn't reached
const BATCH_TIMEOUT = 3 * 1000;

// Circuit breaker state
let consecutiveFailures = 0;
const MAX_CONSECUTIVE_FAILURES = 5;

// Test helper function to reset circuit breaker state
export const resetCircuitBreaker = (): void => {
  consecutiveFailures = 0;
};

/**
 * @param AnalyticsConfig
 * The BatchLogger class is used to buffer analytics events to AEL until the MAX_PAYLOAD_SIZE or MAX_BATCH_SIZE is hit.
 * Then, the events are flushed to AEL's batching endpoint at 'r.stripe.com/b'. Events are also flushed
 * when page's visibility state changes.
 */
export class BatchLogger {
  private logEntries: AnalyticsEvent[] = [];

  private clientId: string;

  private trackingUrl: string;

  private batchSize: number;

  private batchTimeout: number;

  private batchTimer: ReturnType<typeof setTimeout> | null;

  constructor(config: AnalyticsConfig) {
    this.clientId = config.clientId;

    this.trackingUrl = batchProdUrl;

    this.batchSize = config.batchSettings?.batchSize
      ? config.batchSettings?.batchSize
      : MAX_BATCH_SIZE;

    this.batchTimeout = config.batchSettings?.batchTimeout
      ? config.batchSettings?.batchTimeout
      : BATCH_TIMEOUT;

    this.batchTimer = null;

    if (typeof document !== 'undefined') {
      // sendBeacon asynchronously sends analytics data to the AEL batching endpoint
      // when the user moves away from the page
      document.addEventListener('visibilitychange', (_evt) => {
        if (
          document.visibilityState === 'hidden' &&
          this.logEntries.length > 0
        ) {
          this.handleLogging();
        }
      });
    }
  }

  /**
   * Function adds an analytics event to an ongoing list, and flushes the events when the list's length in
   * bytes is greater than MAX_PAYLOAD_SIZE or its size is at batchSize.
   */
  public pushEvent(event: AnalyticsEvent): void {
    // if adding event will cause buffer overflow, send current buffer
    if (
      this.getPayloadSizeInBytes([...this.logEntries, event]) >=
      MAX_PAYLOAD_SIZE
    ) {
      this.fetchLogs(false);
    }

    // add event to buffer
    this.logEntries.push(event);

    // if batch size is reached, send buffer
    if (this.logEntries.length >= this.batchSize) {
      this.fetchLogs(false);
    } else if (!this.batchTimer) {
      // Set a timer to send the buffer if the batch size isn't reached
      this.batchTimer = setTimeout(() => {
        this.fetchLogs(false);
      }, this.batchTimeout);
    }
  }

  /**
   * Function returns the buffer of events.
   */
  public getEvents(): AnalyticsEvent[] {
    return this.logEntries;
  }

  /**
   * Function posts the events to the AEL batching endpoint.
   */
  private async fetchLogs(
    sendAsync: boolean,
  ): Promise<Response | null | undefined> {
    if (this.logEntries.length === 0) {
      return;
    }

    // Circuit breaker: stop sending requests after max consecutive failures
    if (consecutiveFailures >= MAX_CONSECUTIVE_FAILURES) {
      this.resetBuffer();
      return;
    }

    const params = this.getLoggerPayload();
    const body = toQueryString(params);

    this.resetBuffer();

    try {
      const result = await fetch(this.trackingUrl, {
        method: 'POST',
        headers: {'Content-Type': 'application/x-www-form-urlencoded'},
        body,
        credentials: 'omit',
        keepalive: sendAsync,
      });

      // Reset failure counter on successful request
      consecutiveFailures = 0;
      return result;
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (err) {
      // Increment failure counter on failed request
      consecutiveFailures++;
      return null;
    }
  }

  /**
   * Function returns a request payload adhering to the AEL batching schema.
   */
  public getLoggerPayload(events = this.logEntries): {
    client_id: string;
    num_requests: number;
    events: AnalyticsEvent[];
  } {
    return {
      client_id: this.clientId,
      num_requests: events.length,
      events: [...events],
    };
  }

  /**
   * Function empties the list of entries.
   */
  public resetBuffer(): void {
    this.logEntries = [];
    if (this.batchTimer) {
      clearTimeout(this.batchTimer);
      this.batchTimer = null;
    }
  }

  /**
   * Function returns the size in bytes of the logged events as a URL-encoded string
   */
  public getPayloadSizeInBytes(
    events: AnalyticsEvent[] = this.logEntries,
  ): number {
    const payload = new Blob([toQueryString(this.getLoggerPayload(events))]);
    return payload.size;
  }

  /**
   * Function checks if sendBeacon is supported.
   */
  private isSendBeaconSupported(): typeof navigator.sendBeacon {
    return navigator && navigator.sendBeacon;
  }

  /**
   * Function uses navigator.sendBeacon() to send logged events to the trackingUrl.
   */
  private sendBeaconWithPayload(): boolean {
    // sendBeacon returns true if the request has successfully been queued for data transfer.
    // A return value of true does NOT mean that the request was successful
    // sendBeacon may return false if the payload is too big (64kb for Chrome)

    // sendBeacon may throw a TypeError if the navigator context isn't bound to the function
    // for example, https://github.com/jehna/ga-lite/issues/380
    // so, we explicitly bind navigator to sendBeacon to avoid running into "TypeError: Illegal invocation"
    // sendBeacon may also throw an error if the input URL is not parseble
    const send = navigator.sendBeacon.bind(navigator);
    try {
      const result = send(
        this.trackingUrl,
        new Blob([toQueryString(this.getLoggerPayload())], {
          type: 'application/x-www-form-urlencoded',
        }),
      );

      return result;
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (err) {
      // return false to default to fetch method
      return false;
    }
  }

  /**
   * Function uses sendBeacon() to send requests. If sendBeacon() isn't supported or returns false,
   * function fallbacks to using fetch instead with keepAlive set to true.
   */
  private handleLogging(): void {
    // stop sending requests after max consecutive failures
    if (consecutiveFailures >= MAX_CONSECUTIVE_FAILURES) {
      this.resetBuffer();
      return;
    }

    let sendBeaconSuccess = false;
    if (this.isSendBeaconSupported()) {
      sendBeaconSuccess = this.sendBeaconWithPayload();
      if (sendBeaconSuccess) {
        consecutiveFailures = 0;
      }
    }

    // Fallback to fetch with keepAlive if sendBeacon isn't supported or the data queuing failed.
    if (!sendBeaconSuccess) {
      this.fetchLogs(true);
    }

    this.resetBuffer();
  }
}

/**
 * copy pasta from https://git.corp.stripe.com/stripe-internal/pay-server/blob/864c835c785133a75d0eb125f6c29024eed379ea/frontend/analytics-js/src/utils/string.ts#L83
 */
export function toQueryString(params: Record<string, unknown>): string {
  let qs = '';

  // Safely loop through keys
  for (const k in params) {
    if (params.hasOwnProperty(k)) {
      // Grab the value
      let v = params[k];

      // If it's an object, serialize it
      if (typeof v === 'object') {
        v = JSON.stringify(v);
      }
      qs += `&${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`;
    }
  }

  return qs.replace('&', '');
}
